import { create } from 'zustand';
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase.js';
import { resolveUserFullName } from '../utils/userUtils.js';
import {
  recordCrmChangeLog,
  computeFieldDiffs,
  describeProductItem,
  resolveAccountFieldSection
} from '../services/changeLogService.js';

/**
 * useAccountStore — Account-Centric Zustand Store for Abacus CRM
 * 
 * Features:
 * - Root account catalog management, search, and filtering
 * - Active account selection and state caching
 * - 6 Real-time subcollection subscriptions (contacts, comments_and_history, products, cases, activities, documents)
 * - Automated listener teardown map (_activeUnsubscribers) preventing memory leaks across account switches
 * - Optimistic local state updates with automatic rollback upon Firestore write failures
 * - High data density UI controls (activeTab, activeSubpanel, search, compact density mode, modals)
 */
export function generateAccountCode(companyName, existingAccounts = []) {
  if (!companyName || !companyName.trim()) return '';
  const clean = companyName.trim().replace(/[^a-zA-Z0-9]/g, '');
  const basePrefix = (clean.substring(0, 4) || 'CUST').toUpperCase().padEnd(4, 'X');
  
  let maxNum = 0;
  existingAccounts.forEach(acc => {
    const accNum = acc.account_number || acc.account_number_c || '';
    if (accNum && accNum.toUpperCase().startsWith(basePrefix)) {
      const numPart = accNum.substring(basePrefix.length);
      if (/^\d+$/.test(numPart)) {
        maxNum = Math.max(maxNum, parseInt(numPart, 10));
      }
    }
  });
  const nextNum = String(maxNum + 1).padStart(3, '0');
  return `${basePrefix}${nextNum}`;
}

export const useAccountStore = create((set, get) => ({
  // =========================================================================
  // 1. ROOT ACCOUNTS STATE
  // =========================================================================
  accounts: [],
  userRole: 'Accounts',
  setUserRole: (role) => set({ userRole: role }),
  filteredAccounts: [],
  selectedAccountId: null,
  selectedAccount: null,
  
  // Loading & Error States (Canonical and Convenience Aliases)
  loading: false,
  isLoadingAccounts: false,
  isLoadingAccount: false,
  isSavingAccount: false,
  error: null,
  accountsError: null,
  accountError: null,

  // Search, Filter & Sort State
  searchTerm: '', globalSearchQuery: '', setGlobalSearchQuery: (q) => set({ globalSearchQuery: q }),
  searchQuery: '', // Alias
  statusFilter: 'All', // 'All' | 'Active' | 'Inactive' | 'Suspended'
  sortField: 'name',   // 'name' | 'updatedAt' | 'createdAt'
  sortDirection: 'asc',// 'asc' | 'desc'

  // =========================================================================
  // 2. SUBCOLLECTIONS STATE (6 Nested Collections)
  // =========================================================================
  contacts: [],
  comments_and_history: [],
  commentsHistory: [], // Compatibility Alias
  products: [],
        financeProducts: [],
  cases: [],
  activities: [],
  documents: [],
  change_logs: [],
  courtesy_calls: [],

  subcollectionLoading: {
    contacts: false,
    comments_and_history: false,
    products: false,
    cases: false,
    activities: false,
    documents: false
  },

  subcollectionErrors: {
    contacts: null,
    comments_and_history: null,
    products: null,
    cases: null,
    activities: null,
    documents: null
  },

  // =========================================================================
  // 3. UI, TABS & MODALS STATE
  // =========================================================================
  activeTab: 'overview', // 'overview' | 'additional' | 'banking'
  activeTopTab: 'overview', // Alias
  activeSubpanel: 'contacts', // 'contacts' | 'comments_and_history' | 'products' | 'cases' | 'activities' | 'documents'
  isCompactDensity: true,
  
  modalState: {
    isOpen: false,
    type: null, // 'account' | 'contact' | 'comment' | 'product' | 'case' | 'activity' | 'document'
    mode: 'create', // 'create' | 'edit'
    initialData: null
  },

  // =========================================================================
  // 4. LISTENER REGISTRY (INTERNAL TEARDOWN MAP)
  // =========================================================================
  _activeUnsubscribers: {},

  // =========================================================================
  // ACTIONS: SEARCH, FILTERING & SORTING
  // =========================================================================
  setSearchTerm: (term) => {
    set({ searchTerm: term, searchQuery: term });
    get().applyFilters();
  },

  setSearchQuery: (queryText) => {
    set({ searchTerm: queryText, searchQuery: queryText });
    get().applyFilters();
  },

  setStatusFilter: (status) => {
    set({ statusFilter: status });
    get().applyFilters();
  },

  setSorting: (field, direction) => {
    set({ sortField: field, sortDirection: direction });
    get().applyFilters();
  },

  applyFilters: () => {
    const { accounts, searchTerm, statusFilter, sortField, sortDirection } = get();
    let result = Array.isArray(accounts) ? [...accounts] : [];

    if (statusFilter && statusFilter !== 'All') {
      result = result.filter((a) => (a.status || 'Active').toLowerCase() === statusFilter.toLowerCase());
    }

    const q = (searchTerm || '').trim().toLowerCase();
    if (q) {
      result = result.filter((a) => {
        const addressStr = [
          a.billing_address_street,
          a.billing_address_city,
          a.billing_address_suburb,
          a.billing_address_state,
          a.billing_address_postal_code,
          a.billing_address_postalcode,
          a.billing_address_country,
          a.shipping_address_street,
          a.shipping_address_city,
          a.shipping_address_suburb,
          a.shipping_address_state,
          a.shipping_address_postal_code,
          a.shipping_address_postalcode,
          a.shipping_address_country,
          a.billing_address?.street,
          a.billing_address?.city,
          a.billing_address?.suburb,
          a.billing_address?.state,
          a.billing_address?.postal_code,
          a.billing_address?.country,
          a.shipping_address?.street,
          a.shipping_address?.city,
          a.shipping_address?.suburb,
          a.shipping_address?.state,
          a.shipping_address?.postal_code,
          a.shipping_address?.country
        ].filter(Boolean).join(' ').toLowerCase();

        return (
          (a.name || '').toLowerCase().includes(q) ||
          (a.account_number || '').toLowerCase().includes(q) ||
          (a.phone_office || '').toLowerCase().includes(q) ||
          (a.phone_alternate || '').toLowerCase().includes(q) ||
          (a.email || '').toLowerCase().includes(q) ||
          (a.billing_email || '').toLowerCase().includes(q) ||
          (a.banking?.email || '').toLowerCase().includes(q) ||
          (a.registered_business_name || '').toLowerCase().includes(q) ||
          (a.splynxlink || '').toLowerCase().includes(q) ||
          (a.pabx_ip || '').toLowerCase().includes(q) ||
          (a.website || '').toLowerCase().includes(q) ||
          (a.vat_number || '').toLowerCase().includes(q) ||
          (a.company_registration_number || '').toLowerCase().includes(q) ||
          (a.registration_number || '').toLowerCase().includes(q) ||
          (a.id_number || '').toLowerCase().includes(q) ||
          (a.industry || '').toLowerCase().includes(q) ||
          addressStr.includes(q)
        );
      });
    }

    result.sort((a, b) => {
      let valA = a[sortField] || '';
      let valB = b[sortField] || '';
      if (typeof valA === 'string' && typeof valB === 'string') {
        return sortDirection === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }
      return sortDirection === 'asc' ? (valA > valB ? 1 : -1) : (valA < valB ? 1 : -1);
    });

    set({ filteredAccounts: result });
  },

  // =========================================================================
  // ACTIONS: FETCH ACCOUNTS
  // =========================================================================
  fetchAccounts: async () => {
    set({ loading: true, isLoadingAccounts: true, error: null, accountsError: null });
    try {
      const accountsColl = collection(db, 'accounts');
      const q = query(accountsColl, orderBy('name', 'asc'));
      const snapshot = await getDocs(q);

      const accountsData = [];
      snapshot.forEach((docSnap) => {
        accountsData.push({ id: docSnap.id, ...docSnap.data() });
      });

      set({
        accounts: accountsData,
        loading: false,
        isLoadingAccounts: false,
        accountsError: null
      });
      get().applyFilters();
      return accountsData;
    } catch (err) {
      console.error('[useAccountStore] Failed to fetch accounts:', err);
      const errMsg = err?.message || 'Failed to fetch accounts';
      set({
        loading: false,
        isLoadingAccounts: false,
        error: errMsg,
        accountsError: errMsg
      });
      return [];
    }
  },

  // =========================================================================
  // ACTIONS: ACCOUNT SELECTION & REAL-TIME SNAPSHOT SYNC
  // =========================================================================
  selectAccount: async (accountId) => {
    if (!accountId) {
      get().clearSelection();
      return;
    }

    // 1. Cleanup all existing active listeners before creating new subscriptions
    get().cleanupListeners();

    set({
      selectedAccountId: accountId,
      loading: true,
      isLoadingAccount: true,
      error: null,
      accountError: null,
      contacts: [],
      comments_and_history: [],
      commentsHistory: [],
      products: [],
        financeProducts: [],
      cases: [],
      activities: [],
      documents: []
    });

    try {
      const newUnsubs = {};

      // 2. Subscribe to Root Account Document & Linked Finance Products
      let currentFinanceUnsub = null;
      let lastSyncedQboId = null;

      const syncFinanceProducts = (qboClientId) => {
        if (qboClientId === lastSyncedQboId && currentFinanceUnsub) return;
        lastSyncedQboId = qboClientId;

        if (currentFinanceUnsub) {
          currentFinanceUnsub();
          currentFinanceUnsub = null;
          delete newUnsubs['financeProducts'];
        }
        if (!qboClientId) {
          set({ financeProducts: [] });
          return;
        }

        const collRef = collection(db, 'client_services');
        const q = query(collRef, where('qboClientId', '==', qboClientId));
        currentFinanceUnsub = onSnapshot(q, (snap) => {
          const arr = [];
          snap.forEach(d => arr.push({ id: d.id, ...d.data() }));
          set({ financeProducts: arr });
        });
        newUnsubs['financeProducts'] = currentFinanceUnsub;
      };

      const accountDocRef = doc(db, 'accounts', accountId);
      const unsubAccount = onSnapshot(
        accountDocRef,
        (docSnap) => {
          if (docSnap.exists()) {
            const data = { id: docSnap.id, ...docSnap.data() };
            set({
              selectedAccount: data,
              loading: false,
              isLoadingAccount: false,
              error: null,
              accountError: null
            });
            syncFinanceProducts(data.qbo_client_id || data.qboClientId || null);
          } else {
            set({
              selectedAccount: null,
              loading: false,
              isLoadingAccount: false,
              error: 'Account not found',
              accountError: 'Account not found',
              financeProducts: []
            });
            syncFinanceProducts(null);
          }
        },
        (err) => {
          console.error(`[useAccountStore] Account listener error [${accountId}]:`, err);
          const errMsg = err?.message || 'Error syncing account document';
          set({
            loading: false,
            isLoadingAccount: false,
            error: errMsg,
            accountError: errMsg
          });
        }
      );
      
      newUnsubs['account'] = unsubAccount;


      // 3. Define Subcollections with Sorting Specs
      const subcollectionSpecs = [
        { name: 'contacts', key: 'contacts', orderField: 'full_name', orderDir: 'asc' },
        { name: 'comments_and_history', key: 'comments_and_history', orderField: 'createdAt', orderDir: 'desc' },
        { name: 'products', key: 'products', orderField: 'product_name', orderDir: 'asc' },
        { name: 'cases', key: 'cases', orderField: 'createdAt', orderDir: 'desc' },
        { name: 'activities', key: 'activities', orderField: 'due_date', orderDir: 'asc' },
        { name: 'documents', key: 'documents', orderField: 'createdAt', orderDir: 'desc' },
        { name: 'change_logs', key: 'change_logs', orderField: 'createdAt', orderDir: 'desc' },
        { name: 'courtesy_calls', key: 'courtesy_calls', orderField: 'calledAt', orderDir: 'desc' }
      ];

      // 4. Attach parallel onSnapshot listeners for each subcollection
      subcollectionSpecs.forEach((spec) => {
        set((state) => ({
          subcollectionLoading: { ...state.subcollectionLoading, [spec.name]: true },
          subcollectionErrors: { ...state.subcollectionErrors, [spec.name]: null }
        }));

        const subCollRef = collection(db, 'accounts', accountId, spec.name);

        const unsubSub = onSnapshot(
          subCollRef,
          (snapshot) => {
            const items = [];
            snapshot.forEach((d) => {
              items.push({ id: d.id, ...d.data() });
            });

            // Robust in-memory sorting ensuring no documents are dropped by missing Firestore fields
            items.sort((a, b) => {
              if (spec.key === 'comments_and_history' || spec.key === 'cases' || spec.key === 'documents' || spec.key === 'change_logs') {
                const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : new Date(a.createdAt || a.date_entered || a.updatedAt || 0).getTime();
                const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : new Date(b.createdAt || b.date_entered || b.updatedAt || 0).getTime();
                return spec.orderDir === 'desc' ? timeB - timeA : timeA - timeB;
              }
              const valA = String(a[spec.orderField] || '').toLowerCase();
              const valB = String(b[spec.orderField] || '').toLowerCase();
              return spec.orderDir === 'desc' ? valB.localeCompare(valA) : valA.localeCompare(valB);
            });

            set((state) => {
              const updates = {
                [spec.key]: items,
                subcollectionLoading: { ...state.subcollectionLoading, [spec.name]: false },
                subcollectionErrors: { ...state.subcollectionErrors, [spec.name]: null }
              };
              if (spec.key === 'comments_and_history') {
                updates.commentsHistory = items;
              }
              return updates;
            });
          },
          (err) => {
            console.error(`[useAccountStore] Subcollection [${spec.name}] sync error:`, err);
            set((state) => ({
              subcollectionLoading: { ...state.subcollectionLoading, [spec.name]: false },
              subcollectionErrors: { ...state.subcollectionErrors, [spec.name]: err?.message || 'Sync error' }
            }));
          }
        );

        newUnsubs[spec.name] = unsubSub;
      });

      // 5. Store active unsubscribers in store state
      set({ _activeUnsubscribers: newUnsubs });
    } catch (err) {
      console.error('[useAccountStore] Error selecting account:', err);
      const errMsg = err?.message || 'Error subscribing to account';
      set({
        loading: false,
        isLoadingAccount: false,
        error: errMsg,
        accountError: errMsg
      });
    }
  },

  // =========================================================================
  // ACTIONS: CLEANUP & SELECTION RESET
  // =========================================================================
  cleanupListeners: () => {
    const { _activeUnsubscribers } = get();
    if (_activeUnsubscribers && typeof _activeUnsubscribers === 'object') {
      Object.entries(_activeUnsubscribers).forEach(([key, unsub]) => {
        if (typeof unsub === 'function') {
          try {
            unsub();
          } catch (e) {
            console.error(`[useAccountStore] Failed to unsubscribe listener [${key}]:`, e);
          }
        }
      });
    }
    set({ _activeUnsubscribers: {} });
  },

  clearSelection: () => {
    get().cleanupListeners();
    set({
      selectedAccountId: null,
      selectedAccount: null,
      loading: false,
      isLoadingAccount: false,
      error: null,
      accountError: null,
      contacts: [],
      comments_and_history: [],
      commentsHistory: [],
      products: [],
        financeProducts: [],
      cases: [],
      activities: [],
      documents: [],
      _activeUnsubscribers: {}
    });
  },

  // =========================================================================
  // ACTIONS: ACCOUNT CRUD WITH OPTIMISTIC UPDATES & ERROR ROLLBACKS
  // =========================================================================
  addAccount: async (accountData) => {
    return await get().createAccount(accountData);
  },

  createAccount: async (newAccountData) => {
    set({ isSavingAccount: true, error: null, accountError: null });
    try {
      const collRef = collection(db, 'accounts');

      // Auto-generate account number: First 4 characters of name (no spaces) + 001, 002...
      const companyName = newAccountData.name || 'UNKN';
      const generatedAccountNumber = newAccountData.account_number && !newAccountData.account_number.startsWith('ACT-')
        ? newAccountData.account_number.toUpperCase().trim()
        : generateAccountCode(companyName, get().accounts);

      const userType = newAccountData.user_type || (newAccountData.user_type_c === 'BusinessUser' ? 'Business' : (newAccountData.user_type_c === 'Supplier' ? 'Supplier' : (newAccountData.user_type_c === 'HomeUser' ? 'Personal' : '')));
      if (!userType) {
        throw new Error('User Type (Business or Personal) is mandatory.');
      }
      const userTypeC = userType === 'Business' ? 'BusinessUser' : (userType === 'Supplier' ? 'Supplier' : 'HomeUser');

      const cleanFolderName = `${companyName} - ${generatedAccountNumber}`.replace(/[\/\\:\*\?"<>\|]/g, '-').trim();
      const drivePath = `G:\\Shared drives\\Abacus Telecomm\\Captured Clients\\${cleanFolderName}`;

      const payload = {
        ...newAccountData,
        user_type: userType,
        user_type_c: userTypeC,
        account_number: generatedAccountNumber,
        google_drive_folder_name: cleanFolderName,
        google_drive_folder_path: drivePath,
        google_drive_folder_created: false,
        status: newAccountData.status || 'Active',
        account_type: newAccountData.account_type || 'Customer',
        summary_counts: {
          contacts_count: 0,
          active_products_count: 0,
          open_cases_count: 0,
          pending_activities_count: 0,
          documents_count: 0,
          ...(newAccountData.summary_counts || {})
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collRef, payload);
      const newId = docRef.id;

      set({ isSavingAccount: false });
      await get().fetchAccounts();
      await get().selectAccount(newId);
      return { id: newId, account_number: generatedAccountNumber, ...payload };
    } catch (err) {
      console.error('[useAccountStore] Failed to create account:', err);
      const errMsg = err?.message || 'Failed to create account';
      set({ isSavingAccount: false, error: errMsg, accountError: errMsg });
      throw err;
    }
  },

  linkQboCustomer: async (accountId, qboClientId, qboClientName) => {
    try {
      const docRef = doc(db, 'accounts', accountId);
      await updateDoc(docRef, {
        qbo_client_id: String(qboClientId),
        qbo_client_name: qboClientName,
        qbo_synced_at: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      set((state) => {
        const updatedAccounts = state.accounts.map((a) =>
          a.id === accountId ? { ...a, qbo_client_id: String(qboClientId), qbo_client_name: qboClientName } : a
        );
        const updatedSelected = state.selectedAccount && state.selectedAccount.id === accountId
          ? { ...state.selectedAccount, qbo_client_id: String(qboClientId), qbo_client_name: qboClientName }
          : state.selectedAccount;
        return {
          accounts: updatedAccounts,
          selectedAccount: updatedSelected
        };
      });
      return true;
    } catch (err) {
      console.error('[useAccountStore] linkQboCustomer error:', err);
      throw err;
    }
  },

  updateAccount: async (accountId, updatedFields) => {
    if (!accountId) throw new Error('Account ID is required for update');

    const fieldsToUpdate = { ...updatedFields };
    if (fieldsToUpdate.user_type) {
      fieldsToUpdate.user_type_c = fieldsToUpdate.user_type === 'Business' ? 'BusinessUser' : (fieldsToUpdate.user_type === 'Supplier' ? 'Supplier' : 'HomeUser');
    } else if (fieldsToUpdate.user_type_c) {
      fieldsToUpdate.user_type = fieldsToUpdate.user_type_c === 'BusinessUser' ? 'Business' : (fieldsToUpdate.user_type_c === 'Supplier' ? 'Supplier' : 'Personal');
    }

    // 1. Snapshot previous state for rollback
    const prevAccount = get().selectedAccount ? { ...get().selectedAccount } : null;
    const prevAccounts = [...get().accounts];

    // 2. Optimistic local update
    set((state) => ({
      isSavingAccount: true,
      error: null,
      accountError: null,
      selectedAccount:
        state.selectedAccount && state.selectedAccount.id === accountId
          ? { ...state.selectedAccount, ...fieldsToUpdate }
          : state.selectedAccount,
      accounts: state.accounts.map((a) => (a.id === accountId ? { ...a, ...fieldsToUpdate } : a))
    }));
    get().applyFilters();

    // 3. Commit to Firestore
    try {
      const docRef = doc(db, 'accounts', accountId);
      await updateDoc(docRef, {
        ...fieldsToUpdate,
        updatedAt: serverTimestamp()
      });
      set({ isSavingAccount: false });

      const baseAcc = (prevAccount && prevAccount.id === accountId) ? prevAccount : (prevAccounts.find((a) => a.id === accountId) || {});
      const accDiffs = computeFieldDiffs(baseAcc, fieldsToUpdate);
      accDiffs.forEach((diff) => {
        recordCrmChangeLog({
          accountId,
          accountName: baseAcc.name || fieldsToUpdate.name || 'Account',
          section: resolveAccountFieldSection(diff.field),
          action: 'Edited',
          itemName: baseAcc.name || fieldsToUpdate.name || 'Account',
          fieldLabel: diff.label,
          previousValue: diff.from,
          newValue: diff.to
        });
      });
    } catch (err) {
      console.error('[useAccountStore] Failed to update account, rolling back:', err);
      const errMsg = err?.message || 'Failed to update account';
      // 4. Rollback on failure
      set({
        selectedAccount: prevAccount,
        accounts: prevAccounts,
        isSavingAccount: false,
        error: errMsg,
        accountError: errMsg
      });
      get().applyFilters();
      throw err;
    }
  },

  deleteAccount: async (accountId) => {
    if (!accountId) throw new Error('Account ID is required for delete');

    // 1. Snapshot previous state for rollback
    const prevAccounts = [...get().accounts];
    const prevSelectedId = get().selectedAccountId;
    const prevSelectedAccount = get().selectedAccount ? { ...get().selectedAccount } : null;

    // 2. Optimistic local deletion
    set((state) => ({
      accounts: state.accounts.filter((a) => a.id !== accountId),
      ...(state.selectedAccountId === accountId
        ? { selectedAccountId: null, selectedAccount: null }
        : {})
    }));
    get().applyFilters();

    if (prevSelectedId === accountId) {
      get().cleanupListeners();
    }

    // 3. Commit delete to Firestore
    try {
      const docRef = doc(db, 'accounts', accountId);
      await deleteDoc(docRef);
    } catch (err) {
      console.error('[useAccountStore] Failed to delete account, rolling back:', err);
      const errMsg = err?.message || 'Failed to delete account';
      // 4. Rollback on failure
      set({
        accounts: prevAccounts,
        selectedAccountId: prevSelectedId,
        selectedAccount: prevSelectedAccount,
        error: errMsg,
        accountError: errMsg
      });
      get().applyFilters();
      throw err;
    }
  },

  // =========================================================================
  // ACTIONS: GENERIC SUBCOLLECTION CRUD OPERATIONS
  // =========================================================================
  addSubcollectionItem: async (subcollectionName, itemData) => {
    const { selectedAccountId, selectedAccount } = get();
    if (!selectedAccountId) throw new Error('No active account selected.');

    const subCollRef = collection(db, 'accounts', selectedAccountId, subcollectionName);
    
    // Clean undefined fields to prevent Firestore serialization crashes
    const cleaned = {};
    Object.keys(itemData || {}).forEach((k) => {
      if (itemData[k] !== undefined) {
        cleaned[k] = itemData[k];
      }
    });

    const payload = {
      ...cleaned,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    const docRef = await addDoc(subCollRef, payload);

    if (subcollectionName !== 'change_logs') {
      const sectionMap = {
        contacts: 'Contacts',
        products: 'Products & Services',
        comments_and_history: 'Comments & History',
        cases: 'Cases & Tickets',
        activities: 'Activities',
        documents: 'Documents'
      };
      const section = sectionMap[subcollectionName] || subcollectionName;
      let itemName = '';
      let details = '';
      if (subcollectionName === 'contacts') {
        itemName = cleaned.full_name || `${cleaned.first_name || ''} ${cleaned.last_name || ''}`.trim() || 'Contact';
        details = [cleaned.title, cleaned.phone_mobile || cleaned.phone_work, cleaned.email].filter(Boolean).join(' · ');
      } else if (subcollectionName === 'products') {
        itemName = describeProductItem(cleaned);
      } else if (subcollectionName === 'documents') {
        itemName = cleaned.title || cleaned.file_name || cleaned.name || 'Document';
        details = cleaned.category || cleaned.document_type || '';
      } else if (subcollectionName === 'cases') {
        itemName = cleaned.subject || cleaned.case_number || 'Case';
        details = [cleaned.category, cleaned.priority, cleaned.status].filter(Boolean).join(' · ');
      } else if (subcollectionName === 'comments_and_history') {
        itemName = (cleaned.content || 'Comment').slice(0, 80);
      } else {
        itemName = cleaned.subject || cleaned.title || cleaned.name || 'Record';
      }
      recordCrmChangeLog({
        accountId: selectedAccountId,
        accountName: selectedAccount?.name || 'Account',
        section,
        action: 'Added',
        itemName,
        details,
        userEmail: cleaned.author_email || cleaned.uploaded_by_email,
        userName: cleaned.author_name
      });
    }

    return docRef;
  },

  updateSubcollectionItem: async (subcollectionName, itemId, updatedFields) => {
    const { selectedAccountId, selectedAccount } = get();
    if (!selectedAccountId) throw new Error('No active account selected.');
    if (!itemId) throw new Error('Item ID is required for subcollection update.');

    const existingList = get()[subcollectionName] || [];
    const existingItem = existingList.find((i) => i.id === itemId) || {};

    const docRef = doc(db, 'accounts', selectedAccountId, subcollectionName, itemId);
    
    // Clean undefined fields to prevent Firestore serialization crashes
    const cleaned = {};
    Object.keys(updatedFields || {}).forEach((k) => {
      if (updatedFields[k] !== undefined) {
        cleaned[k] = updatedFields[k];
      }
    });

    await updateDoc(docRef, {
      ...cleaned,
      updatedAt: serverTimestamp()
    });

    if (subcollectionName !== 'change_logs') {
      const sectionMap = {
        contacts: 'Contacts',
        products: 'Products & Services',
        comments_and_history: 'Comments & History',
        cases: 'Cases & Tickets',
        activities: 'Activities',
        documents: 'Documents'
      };
      const section = sectionMap[subcollectionName] || subcollectionName;
      const merged = { ...existingItem, ...cleaned };
      let itemName = '';
      if (subcollectionName === 'contacts') {
        itemName = merged.full_name || `${merged.first_name || ''} ${merged.last_name || ''}`.trim() || 'Contact';
      } else if (subcollectionName === 'products') {
        itemName = describeProductItem(merged);
      } else {
        itemName = merged.title || merged.subject || merged.name || merged.case_number || 'Record';
      }

      const diffs = computeFieldDiffs(existingItem, cleaned).filter(
        (d) => !(subcollectionName === 'contacts' && d.field === 'full_name' && (cleaned.first_name !== undefined || cleaned.last_name !== undefined))
      );

      if (diffs.length > 0 && diffs.length <= 4) {
        diffs.forEach((diff) => {
          recordCrmChangeLog({
            accountId: selectedAccountId,
            accountName: selectedAccount?.name || 'Account',
            section,
            action: 'Edited',
            itemName,
            fieldLabel: diff.label,
            previousValue: diff.from,
            newValue: diff.to
          });
        });
      } else if (diffs.length > 4) {
        recordCrmChangeLog({
          accountId: selectedAccountId,
          accountName: selectedAccount?.name || 'Account',
          section,
          action: 'Edited',
          itemName,
          fieldLabel: `${diffs.length} fields updated`,
          previousValue: diffs[0].from,
          newValue: diffs[0].to,
          changes: diffs
        });
      }
    }
  },

  deleteSubcollectionItem: async (subcollectionName, itemId) => {
    const { selectedAccountId, selectedAccount } = get();
    if (!selectedAccountId) throw new Error('No active account selected.');
    if (!itemId) throw new Error('Item ID is required for subcollection delete.');

    const existingList = get()[subcollectionName] || [];
    const existingItem = existingList.find((i) => i.id === itemId) || {};

    const docRef = doc(db, 'accounts', selectedAccountId, subcollectionName, itemId);
    await deleteDoc(docRef);

    if (subcollectionName !== 'change_logs') {
      const sectionMap = {
        contacts: 'Contacts',
        products: 'Products & Services',
        comments_and_history: 'Comments & History',
        cases: 'Cases & Tickets',
        activities: 'Activities',
        documents: 'Documents'
      };
      const section = sectionMap[subcollectionName] || subcollectionName;
      let itemName = '';
      let details = '';
      if (subcollectionName === 'products') {
        itemName = describeProductItem(existingItem);
      } else if (subcollectionName === 'contacts') {
        itemName = existingItem.full_name || `${existingItem.first_name || ''} ${existingItem.last_name || ''}`.trim() || 'Contact';
        details = [existingItem.title, existingItem.phone_mobile || existingItem.phone_work, existingItem.email].filter(Boolean).join(' · ');
      } else if (subcollectionName === 'documents') {
        itemName = existingItem.title || existingItem.file_name || existingItem.name || 'Document';
        details = existingItem.category || existingItem.document_type || '';
      } else if (subcollectionName === 'cases') {
        itemName = existingItem.subject || existingItem.case_number || 'Case';
      } else if (subcollectionName === 'comments_and_history') {
        itemName = (existingItem.content || 'Comment').slice(0, 80);
      } else {
        itemName = existingItem.subject || existingItem.title || existingItem.name || 'Record';
      }

      recordCrmChangeLog({
        accountId: selectedAccountId,
        accountName: selectedAccount?.name || 'Account',
        section,
        action: 'Deleted',
        itemName,
        details
      });
    }
  },

  // =========================================================================
  // ACTIONS: SPECIALIZED SUBCOLLECTION HELPERS
  // =========================================================================
  // 1. Contacts
  addContact: (data) => {
    const firstName = (data.first_name || '').trim();
    const lastName = (data.last_name || '').trim();
    const fullName = `${firstName} ${lastName}`.trim();
    return get().addSubcollectionItem('contacts', {
      ...data,
      first_name: firstName,
      last_name: lastName,
      full_name: fullName,
      email: (data.email || '').trim().toLowerCase(),
      phone_work: (data.phone_work || '').trim(),
      phone_mobile: (data.phone_mobile || '').trim(),
      whatsapp_number: (data.whatsapp_number || '').trim(),
      is_primary_contact: Boolean(data.is_primary_contact),
      is_billing_contact: Boolean(data.is_billing_contact),
      is_technical_contact: Boolean(data.is_technical_contact),
      status: data.status || 'Active'
    });
  },
  updateContact: (id, data) => {
    const updates = { ...data };
    if (data.first_name !== undefined) updates.first_name = data.first_name.trim();
    if (data.last_name !== undefined) updates.last_name = data.last_name.trim();
    if (data.email !== undefined) updates.email = data.email.trim().toLowerCase();
    if (data.phone_work !== undefined) updates.phone_work = (data.phone_work || '').trim();
    if (data.phone_mobile !== undefined) updates.phone_mobile = (data.phone_mobile || '').trim();
    if (data.whatsapp_number !== undefined) updates.whatsapp_number = (data.whatsapp_number || '').trim();
    if (data.first_name !== undefined || data.last_name !== undefined) {
      const currentContact = get().contacts.find((c) => c.id === id);
      const fn = updates.first_name !== undefined ? updates.first_name : (currentContact?.first_name || '');
      const ln = updates.last_name !== undefined ? updates.last_name : (currentContact?.last_name || '');
      updates.full_name = `${fn} ${ln}`.trim();
    }
    return get().updateSubcollectionItem('contacts', id, updates);
  },
  deleteContact: (id) => get().deleteSubcollectionItem('contacts', id),

  // 2. Comments and History
  addComment: (data, userEmail, userName) => {
    const authorEmail = (typeof userEmail === 'object' ? userEmail?.email : userEmail) || 'technician@abacustelecomm.co.za';
    const authorName = resolveUserFullName(userName || (typeof userEmail === 'object' ? userEmail : null), authorEmail);

    return get().addSubcollectionItem('comments_and_history', {
      ...data,
      type: data.type || 'user_note',
      content: data.content || '',
      pinned: Boolean(data.pinned),
      author_email: authorEmail,
      author_name: authorName
    });
  },
  updateComment: (id, data) => get().updateSubcollectionItem('comments_and_history', id, data),
  deleteComment: (id) => get().deleteSubcollectionItem('comments_and_history', id),

  // 3. Products
  addProduct: (data, userEmail, userName) => {
    const qty = Number(data.quantity) || 1;
    const price = Number(data.unit_price_ex_vat) || 0;
    const authorEmail = (typeof userEmail === 'object' ? userEmail?.email : userEmail) || 'technician@abacustelecomm.co.za';
    const authorName = resolveUserFullName(userName || (typeof userEmail === 'object' ? userEmail : null), authorEmail);

    return get().addSubcollectionItem('products', {
      ...data,
      quantity: qty,
      unit_price_ex_vat: price,
      unit_cost_ex_vat: Number(data.unit_cost_ex_vat) || 0,
      total_price_ex_vat: qty * price,
      billing_frequency: data.billing_frequency || 'Monthly',
      status: data.status || 'Active',
      author_email: authorEmail,
      author_name: authorName
    });
  },
  updateProduct: (id, data) => {
    const updates = { ...data };
    if (data.quantity !== undefined || data.unit_price_ex_vat !== undefined) {
      const currentProduct = get().products.find((p) => p.id === id);
      const qty = Number(data.quantity !== undefined ? data.quantity : currentProduct?.quantity) || 1;
      const price = Number(data.unit_price_ex_vat !== undefined ? data.unit_price_ex_vat : currentProduct?.unit_price_ex_vat) || 0;
      updates.total_price_ex_vat = qty * price;
    }
    return get().updateSubcollectionItem('products', id, updates);
  },
  deleteProduct: (id) => get().deleteSubcollectionItem('products', id),

  // 4. Cases
  addCase: (data) =>
    get().addSubcollectionItem('cases', {
      ...data,
      case_number: data.case_number || `CASE-${Date.now().toString().slice(-6)}`,
      category: data.category || 'Technical Support',
      priority: data.priority || 'Medium',
      status: data.status || 'New',
      date_opened: data.date_opened || new Date().toISOString()
    }),
  updateCase: (id, data) => get().updateSubcollectionItem('cases', id, data),
  deleteCase: (id) => get().deleteSubcollectionItem('cases', id),

  // 5. Activities
  addActivity: (data) =>
    get().addSubcollectionItem('activities', {
      ...data,
      activity_type: data.activity_type || 'task',
      status: data.status || 'Planned',
      priority: data.priority || 'Medium',
      due_date: data.due_date || new Date().toISOString()
    }),
  updateActivity: (id, data) => get().updateSubcollectionItem('activities', id, data),
  deleteActivity: (id) => get().deleteSubcollectionItem('activities', id),

  // 6. Documents
  addDocument: (data, userEmail) =>
    get().addSubcollectionItem('documents', {
      ...data,
      status: data.status || 'Active',
      version: data.version || '1.0',
      uploaded_by_email: userEmail || data.uploaded_by_email || 'technician@abacustelecomm.co.za'
    }),
  updateDocument: (id, data) => get().updateSubcollectionItem('documents', id, data),
  deleteDocument: (id) => get().deleteSubcollectionItem('documents', id),

  // =========================================================================
  // ACTIONS: UI NAVIGATION, TABS & MODALS
  // =========================================================================
  setActiveTab: (tab) => set({ activeTab: tab, activeTopTab: tab }),
  setActiveTopTab: (tab) => set({ activeTab: tab, activeTopTab: tab }),
  setActiveSubpanel: (subpanel) => set({ activeSubpanel: subpanel }),
  toggleDensity: () => set((state) => ({ isCompactDensity: !state.isCompactDensity })),
  setCompactDensity: (isCompact) => set({ isCompactDensity: Boolean(isCompact) }),

  openModal: (type, mode = 'create', initialData = null) =>
    set({
      modalState: {
        isOpen: true,
        type,
        mode,
        initialData
      }
    }),

  closeModal: () =>
    set({
      modalState: {
        isOpen: false,
        type: null,
        mode: 'create',
        initialData: null
      }
    })
}));

export default useAccountStore;
